import { Injectable } from '@angular/core';
import { Observable, of, Subject, throwError as observableThrowError } from 'rxjs';
import { tap } from 'rxjs/operators';

interface CacheData {
	expiry: number;
	value: any;
}

@Injectable()
export class CacheService {
	private cache: Map<string, CacheData> = new Map<string, CacheData>();
	private inFlightObservables: Map<string, Subject<any>> = new Map<string, Subject<any>>();
	readonly DEFAULT_MAX_AGE: number = 30000;

	private hasValidCachedValue(key: string): boolean {
		if (this.cache.has(key)) {
			const cachedData = this.cache.get(key);
			if (cachedData !== undefined) {
				if (cachedData.expiry < Date.now()) {
					this.cache.delete(key);
					return false;
				}
				return true;
			}
		}
		return false;
	}

	public invalidCache(key: string): void {
		if (this.cache.has(key)) {
			this.cache.delete(key);
		}
	}

	private notifyInFlightObservers(key: string, value: any): void {
		if (this.inFlightObservables.has(key)) {
			const inFlight = this.inFlightObservables.get(key);

			if (inFlight !== undefined) {
				if (inFlight.observers.length) {
					console.info(
						`%cNotifying ${inFlight.observers.length} flight subscribers for ${key}`,
						'color:blue',
					);
					inFlight.next(value);
				}
				inFlight.complete();
				this.inFlightObservables.delete(key);
			}
		}
	}

	get(key: string, fallback?: Observable<any>, maxAge = this.DEFAULT_MAX_AGE): Observable<any> | Subject<any> {
		if (this.hasValidCachedValue(key)) {
			console.info(`%cGetting from cache ${key}`, 'color: green');
			const cachedData = this.cache.get(key);
			if (cachedData !== undefined) {
				return of(cachedData.value);
			}
		}

		if (this.inFlightObservables.has(key)) {
			const value = this.inFlightObservables.get(key);
			if (value !== undefined) {
				return value;
			}
		} else if (fallback && fallback instanceof Observable) {
			console.info(`%cCalling api for ${key}`, 'color: purple');
			this.inFlightObservables.set(key, new Subject());
			return fallback.pipe(tap((value) => this.set(key, value, maxAge)));
		}

		return observableThrowError('Requested key is not available in Cache');
	}

	set(key: string, value: any, maxAge: number = this.DEFAULT_MAX_AGE): void {
		this.cache.set(key, { value: value, expiry: Date.now() + maxAge });
		this.notifyInFlightObservers(key, value);
	}
}
